import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useRef, useState, useMemo } from 'react';
import { Box, Typography, Button, Alert, TextField } from '@weave-mui/material';
import { CircularProgress, Checkbox, FormControlLabel, FormGroup, Grid } from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import PermissionButton from '../../components/PermissionButton';
import { Actions, usePermissions } from '../../hooks/usePermissions';
import { api } from '../../api/apiClient';
import { queryKeys } from '../../query/queryClient';
import { parseCollectionIds, parseCsvCollectionIds } from '../../utils/collectionIds';
const BATCH_ID_SUFFIX_PATTERN = /^[A-Za-z0-9-]+$/;
function buildDefaultRollbackScanBatchId() {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `rollback-scan-${date}-${Date.now()}`;
}
export function resolveRollbackScanBatchId(optionalJiraKey) {
    const trimmed = optionalJiraKey.trim();
    if (!trimmed)
        return buildDefaultRollbackScanBatchId();
    const suffix = trimmed.replace(/^rollback-scan-/i, '');
    return `rollback-scan-${suffix}`;
}
const CreateScanJobTab = () => {
    const { canWrite } = usePermissions();
    const fileInputRef = useRef(null);
    const [batchIdInput, setBatchIdInput] = useState('');
    const [collectionsText, setCollectionsText] = useState('');
    const [selectedCases, setSelectedCases] = useState({});
    const [errors, setErrors] = useState({});
    const [successJobId, setSuccessJobId] = useState(null);
    const [successWarning, setSuccessWarning] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const parsedCollectionIds = useMemo(() => parseCollectionIds(collectionsText), [collectionsText]);
    const { data: refreshTimeData, isLoading: isLoadingRefreshTime } = useQuery({
        queryKey: queryKeys.dataFix.dbRefreshTime(),
        queryFn: api.dataFix.getDbRefreshTime,
    });
    const snapshotTimeValue = refreshTimeData?.dbRefreshTime ?? new Date().toISOString().split('T')[0];
    const { data: checkpointsData, isLoading: isLoadingCheckpoints, isError: isCheckpointsError, } = useQuery({
        queryKey: ['dataScan', 'checkpoints'],
        queryFn: () => api.dataScan.getCheckpoints(),
    });
    const checkpoints = checkpointsData?.checkpoints ?? [];
    const createJobMutation = useMutation({
        mutationFn: (payload) => api.dataFix.createJob(payload),
        onSuccess: (data) => {
            setSuccessJobId(data.jobId);
            setSuccessWarning(data.warning ?? null);
            setSubmitError(null);
            setBatchIdInput('');
            setCollectionsText('');
            setSelectedCases({});
            setErrors({});
        },
        onError: (err) => {
            setSubmitError(err?.message ?? 'Failed to create scan job');
            setSuccessJobId(null);
            setSuccessWarning(null);
        },
    });
    const allSelected = checkpoints.length > 0 && checkpoints.every((c) => selectedCases[c.name]);
    const someSelected = checkpoints.some((c) => selectedCases[c.name]);
    const isIndeterminate = someSelected && !allSelected;
    function handleSelectAll(checked) {
        setSelectedCases(Object.fromEntries(checkpoints.map((c) => [c.name, checked])));
    }
    function handleCaseToggle(name, checked) {
        setSelectedCases((prev) => ({ ...prev, [name]: checked }));
    }
    function handleUpload(e) {
        const file = e.target.files?.[0];
        if (!file)
            return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const csvText = ev.target?.result;
            setCollectionsText(parseCsvCollectionIds(csvText).join('\n'));
        };
        reader.readAsText(file);
        e.target.value = '';
    }
    function validate() {
        const errs = {};
        const batchSuffix = batchIdInput.trim().replace(/^rollback-scan-/i, '');
        if (batchIdInput.trim() && !batchSuffix) {
            errs.batchId = 'Batch ID suffix is required when the field is not empty';
        }
        else if (batchIdInput.trim() && !BATCH_ID_SUFFIX_PATTERN.test(batchSuffix)) {
            errs.batchId = 'Jira key may only contain letters, numbers, and hyphens';
        }
        if (parsedCollectionIds.length === 0)
            errs.collections = 'At least one collection ID is required';
        if (!someSelected)
            errs.cases = 'Select at least one case';
        if (isCheckpointsError || checkpoints.length === 0) {
            errs.submit = 'Scan cases are not available. Try again later.';
        }
        else if (isLoadingRefreshTime || !snapshotTimeValue.trim()) {
            errs.submit = 'DB refresh time is not available yet. Try again in a moment.';
        }
        return errs;
    }
    function handleSubmit() {
        if (!canWrite)
            return;
        if (isLoadingCheckpoints || isLoadingRefreshTime)
            return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setErrors({});
        const taskNames = checkpoints.filter((c) => selectedCases[c.name]).map((c) => c.name);
        createJobMutation.mutate({
            batchId: resolveRollbackScanBatchId(batchIdInput),
            collectionIds: parsedCollectionIds,
            taskNames,
            snapshotTime: snapshotTimeValue,
            options: {
                skipDbScan: false,
                isDryRun: false,
                forceRescan: false,
                skipCollectionInputFilter: false,
                skipScanResultUpload: false,
            },
        });
    }
    const catalogLoading = isLoadingCheckpoints || isLoadingRefreshTime;
    return (_jsxs(Box, { sx: { p: 3, maxWidth: 800 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 4, mb: 3 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500 }, children: "Batch ID" }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsx(TextField, { fullWidth: true, size: "small", value: batchIdInput, onChange: (e) => setBatchIdInput(e.target.value), placeholder: "PIM-32479 (optional)", helperText: batchIdInput.trim()
                                    ? `Submitted as ${resolveRollbackScanBatchId(batchIdInput)}`
                                    : 'Leave empty to auto-generate rollback-scan-YYYYMMDD-timestamp' }), errors.batchId && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: errors.batchId }))] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 4, mb: 3 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500, pt: 1 }, children: "Collections List" }), _jsxs(Box, { sx: { flexGrow: 1 }, children: [_jsxs(Box, { sx: { display: 'flex', gap: 1, mb: 1 }, children: [_jsx(Button, { variant: "outlined", size: "small", onClick: () => fileInputRef.current?.click(), children: "Upload..." }), _jsx("input", { ref: fileInputRef, type: "file", accept: ".csv", style: { display: 'none' }, onChange: handleUpload })] }), _jsx(TextField, { multiline: true, rows: 8, fullWidth: true, value: collectionsText, onChange: (e) => setCollectionsText(e.target.value), placeholder: "Enter collection IDs, one per line or comma-separated..." }), parsedCollectionIds.length > 0 && (_jsxs(Typography, { variant: "caption", sx: { mt: 0.5, display: 'block', color: 'text.secondary' }, children: [parsedCollectionIds.length.toLocaleString(), " collections"] })), errors.collections && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: errors.collections }))] })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 4, mb: 4 }, children: [_jsx(Typography, { sx: { minWidth: 160, fontWeight: 500, pt: 1 }, children: "Cases" }), _jsxs(Box, { children: [errors.cases && (_jsx(Alert, { severity: "error", sx: { mb: 1 }, children: errors.cases })), isLoadingCheckpoints ? (_jsx(CircularProgress, { size: 16 })) : isCheckpointsError ? (_jsx(Typography, { color: "error", children: "Failed to load" })) : (_jsxs(_Fragment, { children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: allSelected, indeterminate: isIndeterminate, onChange: (e) => handleSelectAll(e.target.checked) }), label: "Select All" }), _jsx(FormGroup, { children: _jsx(Grid, { container: true, children: checkpoints.map((c) => (_jsx(Grid, { item: true, xs: 6, children: _jsx(FormControlLabel, { control: _jsx(Checkbox, { checked: !!selectedCases[c.name], onChange: (e) => handleCaseToggle(c.name, e.target.checked) }), label: c.displayName }) }, c.name))) }) })] }))] })] }), errors.submit && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: errors.submit })), catalogLoading && (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1, mb: 2 }, children: [_jsx(CircularProgress, { size: 16 }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: "Loading scan configuration\u2026" })] })), successJobId && (_jsxs(Alert, { severity: "success", sx: { mb: 2 }, children: ["Scan job created successfully. Job ID: ", successJobId] })), successWarning && (_jsx(Alert, { severity: "warning", sx: { mb: 2 }, children: successWarning })), submitError && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, children: submitError })), _jsx(PermissionButton, { variant: "contained", requiredAction: Actions.WRITE, onClick: handleSubmit, disabled: createJobMutation.isPending || catalogLoading || isCheckpointsError, children: createJobMutation.isPending ? 'Submitting...' : 'Submit' })] }));
};
export default CreateScanJobTab;
